// The session context and its types, shared by the shell and every page.
// The provider lives in session.tsx.
import { createContext, useContext } from 'react';

import type { Me, Membership } from './api';

export type MeState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; me: Me }
  | { kind: 'error'; error: string };

/** Where to return after a sign-in redirect (see main.tsx). */
export interface SigninState {
  returnTo?: string;
}

export interface Session {
  apiUrl: string;
  authenticated: boolean;
  /** Sign-in redirect or token restore in progress. */
  authLoading: boolean;
  authError?: Error;
  email?: string;
  token?: string;
  me: MeState;
  /** The selected tenant: the remembered one, else the first. */
  membership?: Membership;
  permissions: string[];
  setTenant: (id: string) => void;
  signIn: () => void;
  signOut: () => void;
  /** A fresh sign-in (renews auth_time) before sensitive actions; returns to returnTo. */
  reauthenticate: (returnTo: string) => void;
}

export const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const s = useContext(SessionContext);
  if (!s) throw new Error('useSession outside SessionProvider');
  return s;
}

export function currentMembership(state: MeState, tenantId: string): Membership | undefined {
  if (state.kind !== 'ready') return undefined;
  const { memberships } = state.me;
  return memberships.find((m) => m.tenant_id === tenantId) ?? memberships[0];
}
